import { ErrorHandler, Inject, Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { WindowService } from '../shared/services/window.service';
import { SERVICE_WORKER_API_AVAILABLE } from './service-worker-api-available.injection-token';

@Injectable({
    providedIn: 'root'
})
export class ServiceWorkerInitiatedMessageService {
    private readonly _messages = new Subject<MessageEvent>();
    private readonly initialized: boolean;
    private serviceWorkerContainer: ServiceWorkerContainer;

    constructor(
        private readonly errorHandler: ErrorHandler,
        private readonly windowService: WindowService,
        @Inject(SERVICE_WORKER_API_AVAILABLE) private readonly serviceWorkerApiAvailable: boolean
    ) {
        this.initialized = this.initialize();
    }

    // Use this to create a long lived channel/subscription that keeps waiting for transactional messages from SW and respond to them
    // Returns the subscription created so consumer will have to handle it appropriately
    public waitForTransactionalMessageAndRespond<T>(
        messageFilter: (messageEvent: MessageEvent) => boolean,
        messageProcessor: (messageEvent: MessageEvent) => Promise<T>
    ): Subscription {
        if (!messageFilter || !messageProcessor || !this.initialized) {
            return;
        }

        return this._messages.pipe(
            filter(messageFilter)
        ).subscribe((message: MessageEvent) => {
            messageProcessor(message).then(result => {
                if (message.ports && message.ports.length) {
                    message.ports[0].postMessage(result);
                } else {
                    // If ports are not available, it's possible that message is sent from older SW version, so we post
                    // response directly back from the message event source (filtered to controller messages in initialize below)
                    (message.source as ServiceWorker).postMessage(result);
                }
            }).catch(ex => {
                this.errorHandler.handleError(ex);
            });
        });
    }

    private initialize(): boolean {
        if (!this.serviceWorkerApiAvailable) {
            return false;
        }

        try {
            // In cases where the service worker has been turned off, but we want to make sure that we can receive shutdown
            // messages from the worker, we will still need to setup a `postMessage` communications channel.
            this.serviceWorkerContainer = this.windowService.nativeWindow.navigator.serviceWorker;
            this.serviceWorkerContainer.addEventListener('message', event => {
                this._messages.next(event);
            });
        } catch {
            return false;
        }

        return true;
    }
}
